import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHeroSliders, createHeroSlider, updateHeroSlider, deleteHeroSlider } from '../store/slices/heroSlice';

const HeroManagementPage = () => {
  const dispatch = useDispatch();
  const { heroSliders, loading, error } = useSelector((state) => state.hero);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    buttonText: '',
    buttonLink: '',
    orderNumber: 0,
    isActive: true
  });

  useEffect(() => {
    dispatch(fetchHeroSliders());
  }, [dispatch]);

  const handleOpenDialog = (slider = null) => {
    if (slider) {
      setFormData(slider);
      setEditMode(true);
    } else {
      setFormData({
        title: '',
        description: '',
        buttonText: '',
        buttonLink: '',
        orderNumber: 0,
        isActive: true
      });
      setEditMode(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setFormData({
      title: '',
      description: '',
      buttonText: '',
      buttonLink: '',
      orderNumber: 0,
      isActive: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'isActive' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await dispatch(updateHeroSlider(formData)).unwrap();
      } else {
        await dispatch(createHeroSlider(formData)).unwrap();
      }
      handleCloseDialog();
      dispatch(fetchHeroSliders());
    } catch (err) {
      console.error('Error saving hero slider:', err);
    }
  };

  const handleDelete = async (sliderId) => {
    try {
      await dispatch(deleteHeroSlider(sliderId)).unwrap();
      dispatch(fetchHeroSliders());
    } catch (err) {
      console.error('Error deleting hero slider:', err);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Card sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4">Hero Section Yönetimi</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Yeni Hero Section Ekle
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error.message}
            </Alert>
          )}

          {loading ? (
            <LinearProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Başlık</TableCell>
                    <TableCell>Açıklama</TableCell>
                    <TableCell>Buton Metni</TableCell>
                    <TableCell>Buton Linki</TableCell>
                    <TableCell>Sıra</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell align="right">İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {heroSliders.map((slider) => (
                    <TableRow key={slider.sliderId}>
                      <TableCell>{slider.title}</TableCell>
                      <TableCell>{slider.description}</TableCell>
                      <TableCell>{slider.buttonText}</TableCell>
                      <TableCell>{slider.buttonLink}</TableCell>
                      <TableCell>{slider.orderNumber}</TableCell>
                      <TableCell>
                        {slider.isActive ? (
                          <Typography color="success.main">Aktif</Typography>
                        ) : (
                          <Typography color="error.main">Pasif</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(slider)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(slider.sliderId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Hero Section Düzenle' : 'Yeni Hero Section Ekle'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Başlık"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Açıklama"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buton Metni"
                name="buttonText"
                value={formData.buttonText}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buton Linki"
                name="buttonLink"
                value={formData.buttonLink}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sıra Numarası"
                name="orderNumber"
                type="number"
                value={formData.orderNumber}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                  />
                }
                label="Aktif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Güncelle' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HeroManagementPage;